// The live bubble's stage at a moment (UI-C18, D-24, EM-11). One function of the bubble and the injected
// clock, so the component and T-117 read the same truth.
//  - born after LIVE_BUBBLE_BIRTH_MS of sustained speech (a cough never makes one);
//  - speaking -> writing at speech_end; "still writing" LIVE_BUBBLE_STILL_WRITING_MS after speech_end;
//  - failure LIVE_BUBBLE_FAILURE_MS after speech_end with no segment, or at once on segment_failed;
//  - segment_dropped collapses it quietly; a segment lands the text in the same bubble;
//  - segment_dropped{other_speaker} (MAJLIS-73) fades it out over LIVE_BUBBLE_FADE_MS instead: its last look, no
//    text, no error look, never a jump.
import {
  LIVE_BUBBLE_BIRTH_MS,
  LIVE_BUBBLE_FADE_MS,
  LIVE_BUBBLE_FAILURE_MS,
  LIVE_BUBBLE_STILL_WRITING_MS,
} from "../constants";
import type { Bubble } from "./model";

export type Stage = "speaking" | "writing" | "still_writing" | "failed" | "text";

export interface BubbleView {
  visible: boolean;
  stage: Stage;
  /** speaking: ms since speech_start. writing: the phrase's spoken length in ms. */
  ms: number;
  /** MAJLIS-73: the voice gate took this bubble away; it is fading out (visible only until the fade ends). */
  fading?: boolean;
}

/** Was the speech sustained long enough for a live bubble to be born? */
export function born(b: Bubble, now: number): boolean {
  const spokenUntil = b.endedAt ?? now;
  return spokenUntil - b.startedAt >= LIVE_BUBBLE_BIRTH_MS;
}

export function bubbleView(b: Bubble, now: number): BubbleView {
  switch (b.phase) {
    case "landed":
      return { visible: true, stage: "text", ms: 0 };
    case "dropped":
      if (b.fadeFrom !== undefined && now < b.fadeFrom + LIVE_BUBBLE_FADE_MS && born(b, now)) {
        const spoken = b.durationMs ?? (b.endedAt ?? b.fadeFrom) - b.startedAt;
        return { visible: true, stage: "writing", ms: spoken, fading: true };
      }
      return { visible: false, stage: "writing", ms: 0 };
    case "failed":
      return { visible: b.hadLive && born(b, now), stage: "failed", ms: 0 };
    case "speaking":
      return { visible: born(b, now), stage: "speaking", ms: now - b.startedAt };
    case "writing": {
      const since = now - (b.endedAt ?? now);
      const stage: Stage =
        since >= LIVE_BUBBLE_FAILURE_MS ? "failed" : since >= LIVE_BUBBLE_STILL_WRITING_MS ? "still_writing" : "writing";
      const spoken = b.durationMs ?? (b.endedAt ?? now) - b.startedAt;
      return { visible: born(b, now), stage, ms: spoken };
    }
  }
}

/** Does anything on the canvas still change with time alone? (drives the ticker) */
export function needsTicks(bubbles: Iterable<Bubble>): boolean {
  for (const b of bubbles) if (b.phase === "speaking" || b.phase === "writing") return true;
  return false;
}

/** The next moment a bubble changes stage by time alone (birth, still writing, failure), or null.
 * The canvas re-renders exactly then, so the EM-11 boundaries hold to the millisecond. */
export function nextBoundary(bubbles: Iterable<Bubble>, now: number): number | null {
  let next: number | null = null;
  const consider = (at: number) => {
    if (at > now && (next === null || at < next)) next = at;
  };
  for (const b of bubbles) {
    if (b.phase === "speaking") consider(b.startedAt + LIVE_BUBBLE_BIRTH_MS);
    if (b.phase === "dropped" && b.fadeFrom !== undefined) consider(b.fadeFrom + LIVE_BUBBLE_FADE_MS);
    if (b.phase === "writing" && b.endedAt !== undefined) {
      consider(b.endedAt + LIVE_BUBBLE_STILL_WRITING_MS);
      consider(b.endedAt + LIVE_BUBBLE_FAILURE_MS);
    }
  }
  return next;
}
